import React, { useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Rating,
  TextField,
  Typography,
  Card,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataSuccessAction,
  FetchDataFailureAction,
  fetchData,
  postData,
} from "../../store/actions";

import {
  skillQuestionsDetail,
  viewEmployeeSkillResponse,
  viewSkillWiseEmployeeResponse,
  viewActiveUsers,
  getDirectReports,
  managerVerifySkillResponse,
} from "../../Service/Apis";

import {
  EmployeeSkillSetType,
  SkillWiseEmployeeResponse,
} from "./Types/SkillTypes";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import Loading from "../../components/Loading/Loading";
import { useCustomToast } from "../../components/Methods/SnackBar";

interface Employeetype {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  gender: string;
  roles: [];
  designation: string;
  date_of_birth: string;
  date_of_joining: string;
  date_of_confirmation: string;
  date_of_exit: string;
  email: string;
  password: string;
}

const ratingLabel = [
  { label: "Awareness", color: "#FF5C5C" },
  { label: "Novice", color: "#5C8CFF" },
  { label: "Competent", color: "#5CFF6E" },
  { label: "Professional", color: "#FF9F5C" },
  { label: "Expert", color: "#B85CFF" },
];

const ReviewEmployeesSkills = () => {
  // State Hooks
  const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
  const [skillsData, setSkillsData] = useState<EmployeeSkillSetType[]>([]);

  const [employee, setEmployee] = useState<Employeetype | null>(null);
  const [skill, setSkill] = useState<EmployeeSkillSetType | null>(null);

  const [data, setData] = useState<EmployeeSkillSetType[]>([]);
  const [skillEmployeeResponseData, setSkillEmployeeResponseData] = useState<
    SkillWiseEmployeeResponse[]
  >([]);

  // Skill-wise expanded employee id
  const [expandedSkillEmpId, setExpandedSkillEmpId] = useState<string | number | null>(null);
  const toggleSkillEmpExpand = (id: any) => {
    setExpandedSkillEmpId((prev) => (prev === id ? null : id));
  };

  // Employee-wise inline expand id (match Your Skills behavior)
  const [expandedEmpSkillId, setExpandedEmpSkillId] = useState<string | number | null>(null);

  const [isSkillWiseReview, setIsSkillWiseReview] = useState<boolean>(false);
  const [isEmployeeWiseReview, setIsEmployeeWiseReviewq] =
    useState<boolean>(true);

  // Redux Hooks
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: any) => state.loading.inPageLoading);
  const selectedRole = useSelector((state: any) => state.auth.selectedRole);
  const showToast = useCustomToast();

  const handleRadioChange = (event: any) => {
    const { name, checked } = event.target;
    setIsSkillWiseReview(name === "skillwisereview" ? checked : !checked);
    setIsEmployeeWiseReviewq(
      name === "employeewisereview" ? checked : !checked
    );
    setEmployee(null);
    setSkill(null);
    setExpandedEmpSkillId(null);
  };

  // Toggle inline expand for a skill
  const toggleEmpExpand = (skillId: any) => {
    setExpandedEmpSkillId((prev) => (prev === skillId ? null : skillId));
  };

  // Save manager verification for currently expanded skill
  const handleManagerSaveInline = async (emp: any, index: number) => {
    const payload = {
      user_id: employee?.id,
      responses: (emp.questions as any).map((q: any) => ({
        skill_question_id: q.skill_question_id,
        manager_skill_response: q.manager_skill_response ?? null,
        manager_verified: q.manager_verified ?? false,
      })),
    } as any;
    try {
      await dispatch2(postData(managerVerifySkillResponse, payload));
      const refreshed = await dispatch2(
        fetchData(`${viewEmployeeSkillResponse}?user_id=${employee?.id}`)
      );
      setData(refreshed as EmployeeSkillSetType[]);
      showToast("Saved", "success", true);
      setExpandedEmpSkillId(null);
      setTimeout(() => {
        const el = document.getElementById(`emp-skill-${emp.skill_id}`);
        if (el) {
          const headerOffset = 80;
          const rect = el.getBoundingClientRect();
          const y = rect.top + window.scrollY - headerOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 0);
    } catch (e) {}
  };

  //Handle Employee Change Function
  const handleEmployeeChange = (e: any, values: any) => {
    setEmployee(values);
  };

  const handleSkillChange = (e: any, values: any) => {
    setSkill(values);
  };

  // Calculate Progress Function
  const calculateProgress = (questions: any) => {
    const attemptedCount = questions.filter(
      (que: any) => que.employee_skill_response !== null
    ).length;
    return (attemptedCount / questions.length) * 100;
  };

  useEffect(() => {
    // API Call get the User Details.
    const fetchUserDetails = async () => {
      try {
        dispatch(setIsLoading(true));
        const endpoint = selectedRole?.toLowerCase() === "manager" ? getDirectReports : viewActiveUsers;
        const response = await dispatch2(fetchData(`${endpoint}`));
        const list = Array.isArray(response?.direct_reports) ? response.direct_reports : response;
        setEmployeesData(list as Employeetype[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchUserDetails();

    const fetchSkillsDetails = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(fetchData(`${skillQuestionsDetail}`));
        setSkillsData(response as EmployeeSkillSetType[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    fetchSkillsDetails();
  }, [selectedRole]);

  useEffect(() => {
    // API call to get the employee skill responses.
    const fetchEmployeeSkillResponse = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(
          fetchData(`${viewEmployeeSkillResponse}?user_id=${employee?.id}`)
        );
        setData(response as EmployeeSkillSetType[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    // Check whether the employee is selected before making the API call.
    if (employee) {
      fetchEmployeeSkillResponse();
    }
  }, [employee]);

  useEffect(() => {
    const fetchSkillEmployeeResponse = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(
          fetchData(
            `${viewSkillWiseEmployeeResponse}?skill_id=${skill?.skill_id}`
          )
        );
        setSkillEmployeeResponseData(response as SkillWiseEmployeeResponse[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };
    if (skill) {
      fetchSkillEmployeeResponse();
    }
  }, [skill]);

  // Loading Spinner Component
  if (isLoading) {
    return (
      <>
        <Loading isLoading={isLoading as boolean} />
      </>
    );
  }

  // Main Component

  return (
    <>
      <Box id="main-component" sx={{ mt: 2, mb: 10 }}>
          <Box sx={{ m: 2, p: 1, display: "flex", justifyContent: "right" }}>
            <RadioGroup>
              <Box sx={{ display: "flex" }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={isEmployeeWiseReview}
                      onChange={handleRadioChange}
                      name="employeewisereview"
                    />
                  }
                  label="Employee Review"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={isSkillWiseReview}
                      onChange={handleRadioChange}
                      name="skillwisereview"
                    />
                  }
                  label="Skills Review"
                />
              </Box>
            </RadioGroup>
          </Box>
          {isEmployeeWiseReview ? (
            <Box>
              <Autocomplete
                size="small"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option.first_name + " " + option.last_name
                }
                value={employee}
                onChange={handleEmployeeChange}
                id="employee-autocomplete"
                options={employeesData}
                sx={{ width: "60%", mx: "auto" }}
                renderInput={(params) => <TextField {...params} label="Select Employee Name" />}
              />
              {employee === null ? null : (
                 <Box sx={{  pt: 2 }}>
                
                  {data.length === 0 ? (
                    <>
                      <Box
                        sx={{
                          mt: 12,
                          left: "50%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <NothingToDisplayComponent />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: "98%",
                          display: "flex",
                          mx: 2,
                          mt: 2,
                        }}
                      >
                        <Box sx={{ width: "20%" }}></Box>
                        <Box sx={{ width: "70%", display: "flex" }}>
                          <Box
                            sx={{
                              width: "90%",
                              mr: 2.7,
                              ml: 1,
                              display: "flex",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {ratingLabel.map((data, index) => {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "20%",
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{ mr: 0.3 }}
                                    fontSize={13}
                                    fontWeight={"bold"}
                                  >
                                    {data.label}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                          <Box sx={{ width: "10%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                            <Typography variant="caption" fontWeight={"bold"}>Average Score</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={13} fontWeight={"bold"}>No. of questions</Typography>
                        </Box>
                      </Box>
                      {data.map((emp, index) => {
                        const totalQuestions = emp.questions.length;
                        const totalPossible = totalQuestions * 5;
                        const managerStars = (emp.questions as any).reduce(
                          (sum: number, q: any) => {
                            const v = parseInt(q?.manager_skill_response || "0", 10);
                            return sum + (isNaN(v) ? 0 : v);
                          },
                          0
                        );
                        const managerPercentage =
                          totalPossible > 0 ? (managerStars / totalPossible) * 100 : 0;
                        const managerRatedCount = (emp.questions as any).filter(
                          (q: any) => q?.manager_skill_response != null && q?.manager_skill_response !== "0"
                        ).length;
                        const isManagerComplete = managerRatedCount === totalQuestions;
                        const empAnswered = emp.questions.filter((q: any) => q.employee_skill_response !== null).length;
                        const isExpanded = expandedEmpSkillId === (emp as any).skill_id;
                        return (
                          <Card key={emp.skill_id} id={`emp-skill-${emp.skill_id}`} sx={{ m: 2, p: 2 }} elevation={4}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <Box sx={{ width: "20%" }}>
                                    <Typography
                                      id={`skill-name-${index}`}
                                      variant="h6"
                                      color="primary"
                                      fontWeight={"bold"}
                                    >
                                      {emp.skill_name}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: "70%",
                                      mx: 2,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    {/* Employee bar */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Typography variant="caption" fontWeight={"bold"} sx={{ width: 70, textAlign: "right" }}>Employee</Typography>
                                  <Box sx={{ width: "100%", position: "relative" }}>
                                        <LinearProgress
                                          variant="determinate"
                                      value={parseFloat(emp.percentage as any)}
                                      sx={{ flexGrow: 1, mr: 2 }}
                                        />
                                        <div
                                          className="skill-info-color"
                                          style={{
                                            position: "absolute",
                                            top: "50%",
                                            transform: `translate(-50%, -50%)`,
                                        left: `${parseFloat(emp.percentage as any) > 50 ? parseFloat(emp.percentage as any) - 1.5 : parseFloat(emp.percentage as any)}%`,
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      </Box>
                                      <Typography variant="body2" fontWeight={"bold"}>
                                        {((parseFloat(emp.percentage as any) / 100) * 5).toFixed(1)}/5
                                      </Typography>
                                    </Box>

                                    {/* Manager bar */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Typography variant="caption" fontWeight={"bold"} sx={{ width: 70, textAlign: "right" }}>Manager</Typography>
                                      <Box sx={{ width: "100%", position: "relative" }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={managerPercentage}
                                          sx={{
                                            flexGrow: 1,
                                            mr: 2,
                                            ...(isManagerComplete
                                              ? {}
                                              : {
                                                  backgroundColor: "#ffebee",
                                              "& .MuiLinearProgress-bar": { backgroundColor: "#d32f2f" },
                                                }),
                                          }}
                                        />
                                        <div
                                          className="skill-info-color"
                                          style={{
                                            position: "absolute",
                                            top: "50%",
                                            transform: `translate(-50%, -50%)`,
                                        left: `${managerPercentage > 50 ? managerPercentage - 1.5 : managerPercentage}%`,
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                        ...(isManagerComplete ? {} : { backgroundColor: "#d32f2f" }),
                                          }}
                                        />
                                      </Box>
                                      <Typography variant="body2" fontWeight={"bold"}>
                                        {((managerPercentage / 100) * 5).toFixed(1)}/5
                                      </Typography>
                                    </Box>
                                  </Box>
                              <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "right" }}>
                                <Typography
                                  variant="h6"
                                  color={
                                    empAnswered === totalQuestions
                                      ? "success"
                                      : "error"
                                  }
                                >
                                  {empAnswered}/{totalQuestions}
                                  </Typography>
                                <ChevronRightIcon
                                  fontSize="medium"
                                  sx={{ cursor: "pointer", ml: 1, transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}
                                  onClick={() => toggleEmpExpand((emp as any).skill_id)}
                                />
                              </Box>
                                </Box>

                            {/* Inline dropdown content */}
                            {isExpanded && (
                              <Box sx={{ mt: 2, p: 1 }}>

                                {(emp.questions as any).map((question: any, questionIndex: number) => (
                                  <Card key={question.skill_question_id} sx={{ mb: 2, p: 2, boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", border: "1px solid #ccc" }} elevation={5}>
                                    <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 160px 160px",
                                          alignItems: "center",
                                          columnGap: 2,
                                        m: 0.5,
                                        p: 0.5,
                                        }}
                                      >
                                      <Box>
                                        <Typography fontWeight={"bold"} variant="h6" sx={{ textAlign: "justify", wordBreak: "break-word" }}>
                                            {questionIndex + 1 + ") "}
                                            {question.question}
                                          </Typography>
                                        </Box>

                                        {/* Employee stars (read-only) */}
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                          <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                          <Rating
                                            name={`emp-rating-${index}-${questionIndex}`}
                                            value={parseInt(question.employee_skill_response || "0")}
                                            readOnly
                                            size="large"
                                            className="rating-color"
                                          />
                                        </Box>

                                        {/* Manager stars (editable for manager) */}
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                          <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                          <Rating
                                            name={`mgr-rating-${index}-${questionIndex}`}
                                            value={parseInt(((question as any).manager_skill_response) || "0")}
                                            readOnly={selectedRole?.toLowerCase() !== 'manager'}
                                            onChange={(event, value) => {
                                              if (selectedRole?.toLowerCase() !== 'manager') return;
                                              const normalized = value && value > 0 ? value.toString() : null;
                                              const currentSkillId = (emp as any).skill_id;
                                              const currentQuestionId = question.skill_question_id;
                                              setData((prev) => prev.map((s) => {
                                                if (s.skill_id !== currentSkillId) return s as any;
                                                const updatedQuestions = (s.questions as any).map((qq: any) => (
                                                  qq.skill_question_id === currentQuestionId
                                                    ? ({
                                                        ...qq,
                                                        manager_skill_response: normalized as any,
                                                        manager_verified: !!value && value > 0,
                                                      } as any)
                                                    : qq
                                                ));
                                                return { ...s, questions: updatedQuestions } as any;
                                              }));
                                            }}
                                            size="large"
                                            className="rating-color"
                                          />
                                        </Box>
                                      </Box>
                                  </Card>
                                ))}
                              {selectedRole?.toLowerCase() === 'manager' && (
                                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                    <Button variant="contained" onClick={() => handleManagerSaveInline(emp, index)}>Save</Button>
                                </Box>
                              )}
                          </Box>
                            )}
                          </Card>
                        );
                      })}
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Autocomplete
                size="small"
                isOptionEqualToValue={(option, value) => option.skill_id === value.skill_id}
                getOptionLabel={(option) => option.skill_name}
                value={skill}
                onChange={handleSkillChange}
                id="skill-autocomplete"
                options={skillsData}
                sx={{ width: "60%", mx: "auto" }}
                renderInput={(params) => <TextField {...params} label="Select Skill Name" />}
              />
              {skill === null ? null : (
                <Box sx={{ mt: 2, pt: 2 }}>
                  {skillEmployeeResponseData.length === 0 ? (
                    <>
                      <Box
                        sx={{
                          mt: 12,
                          left: "50%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <NothingToDisplayComponent />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: "98%",
                          display: "flex",
                          mx: 2,
                          mt: 2,
                        }}
                      >
                        <Box sx={{ width: "20%" }}></Box>
                        <Box sx={{ width: "70%", display: "flex" }}>
                          <Box
                            sx={{
                              width: "90%",
                              mr: 2.7,
                              ml: 1,
                              display: "flex",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {ratingLabel.map((data, index) => {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "20%",
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{ mr: 0.3 }}
                                    fontSize={13}
                                    fontWeight={"bold"}
                                  >
                                    {data.label}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                          <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography fontSize={13} fontWeight={"bold"}>Average score</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={13} fontWeight={"bold"}>No. of questions</Typography>
                      </Box>
                      </Box>
                      {skillEmployeeResponseData[0].employees.map((emp, index) => {
                          const totalQuestions = (emp.questions as any).length;
                          const totalPossible = totalQuestions * 5;
                        const managerStars = (emp.questions as any).reduce((sum: number, q: any) => {
                              const v = parseInt(q?.manager_skill_response || "0", 10);
                              return sum + (isNaN(v) ? 0 : v);
                        }, 0);
                        const managerPercentage = totalPossible > 0 ? (managerStars / totalPossible) * 100 : 0;
                        const managerRatedCount = (emp.questions as any).filter((q: any) => q?.manager_skill_response != null && q?.manager_skill_response !== "0").length;
                          const isManagerComplete = managerRatedCount === totalQuestions;
                        const empAnswered = (emp.questions as any).filter((q: any) => q.employee_skill_response !== null).length;
                        const empIdForExpand: any = (emp as any).user_id ?? index;
                        const isExpanded = expandedSkillEmpId === empIdForExpand;
                          return (
                          <Card key={empIdForExpand} sx={{ m: 2, p: 2 }} elevation={4}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                    <Box sx={{ width: "20%" }}>
                                <Typography variant="h6" color="primary" fontWeight={"bold"}>
                                        {emp.employee_name}
                                      </Typography>
                                    </Box>
                              <Box sx={{ width: "70%", mx: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 60px", alignItems: "center", columnGap: 1 }}>
                                        <Typography variant="caption" fontWeight={"bold"} sx={{ textAlign: "right" }}>Employee</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                    <LinearProgress variant="determinate" value={parseFloat(emp.percentage)} sx={{ flexGrow: 1, mr: 2 }} />
                                    <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: `translate(-50%, -50%)`, left: `${parseFloat(emp.percentage) > 50 ? parseFloat(emp.percentage) - 1.5 : parseFloat(emp.percentage)}%`, width: "12px", height: "12px", borderRadius: "50%" }} />
                                        </Box>
                                  <Typography variant="body2" fontWeight={"bold"}>{((parseFloat(emp.percentage as any) / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                      <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 60px", alignItems: "center", columnGap: 1 }}>
                                        <Typography variant="caption" fontWeight={"bold"} sx={{ textAlign: "right" }}>Manager</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                    <LinearProgress variant="determinate" value={managerPercentage} sx={{ flexGrow: 1, mr: 2, ...(isManagerComplete ? {} : { backgroundColor: "#ffebee", "& .MuiLinearProgress-bar": { backgroundColor: "#d32f2f" } }) }} />
                                    <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: `translate(-50%, -50%)`, left: `${managerPercentage > 50 ? managerPercentage - 1.5 : managerPercentage}%`, width: "12px", height: "12px", borderRadius: "50%", ...(isManagerComplete ? {} : { backgroundColor: "#d32f2f" }) }} />
                                        </Box>
                                  <Typography variant="body2" fontWeight={"bold"}>{((managerPercentage / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                    </Box>
                              <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                <Typography variant="h6" color={empAnswered === totalQuestions ? "success" : "error"}>
                                  {empAnswered}/{totalQuestions}
                                </Typography>
                                <ChevronRightIcon fontSize="medium" sx={{ cursor: "pointer", ml: 1, transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} onClick={() => toggleSkillEmpExpand(empIdForExpand)} />
                                  </Box>
                            </Box>
                            {isExpanded && (
                              <Box sx={{ mt: 2, p: 1 }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", alignItems: "center", columnGap: 2, px: 1, py: 0.5 }}>
                                    <Box />
                                  <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                  <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                  </Box>
                                {(emp.questions as any).map((question: any, questionIndex: number) => (
                                  <Card key={question.skill_question_id} sx={{ mb: 2, p: 2, boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", border: "1px solid #ccc" }} elevation={5}>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", alignItems: "center", columnGap: 2, m: 0.5, p: 0.5 }}>
                                      <Box>
                                        <Typography fontWeight={"bold"} variant="h6" sx={{ textAlign: "justify", wordBreak: "break-word" }}>
                                              {questionIndex + 1 + ") "}
                                              {question.question}
                                            </Typography>
                                          </Box>
                                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                        <Rating name={`skillwise-emp-rating-${index}-${questionIndex}`} value={parseInt(question.employee_skill_response || "0")} readOnly size="large" className="rating-color" />
                                      </Box>
                                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                        <Rating name={`skillwise-mgr-rating-${index}-${questionIndex}`} value={parseInt(((question as any).manager_skill_response) || "0")} readOnly size="large" className="rating-color" />
                                      </Box>
                                        </Box>
                                  </Card>
                                ))}
                                      </Box>
                            )}
                          </Card>
                                    );
                                  })}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
      </Box>
    </>
  );
};

export default ReviewEmployeesSkills;
